import { useAtom, useSetAtom } from 'jotai';
import { ReactNode } from 'react';

import { Body, Link, Modal, ModalBody, ModalFooter, ModalHeader } from '@growtherapy/sprout-ui';

import { CopyButton } from '../components/CopyButton';
import { AudioVideoTroubleshootActionItemWrapper as BaseAudioVideoTroubleshootActionItem } from '../components/help-modal/AudioVideoTroubleshootActionItem';
import { HelpModalItem } from '../components/help-modal/HelpModalItem';
import {
  CLIENT_TELEHEALTH_HELP_ARTICLE,
  PROVIDER_CLIENT_NO_SHOW_ARTICLE,
  PROVIDER_TELEHEALTH_HELP_ARTICLE,
} from '../components/help-modal/constants';
import { useInitAvailableDevices } from '../session/devices/useDevices';
import { ProviderDrawerState, drawerStateAtom, providerIsHelpModalOpenAtom } from './state';

type HelpModalProps = {
  isOpen: boolean;
  onClose: () => void;
  audioVideoTroubleshootActionItem?: ReactNode;
};

export function HelpModal({
  isOpen,
  onClose,
  audioVideoTroubleshootActionItem: AudioVideoTroubleshootActionItem,
}: HelpModalProps) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} className='rebrand:bg-surface-inverseEmphasis rebrand:mobile:p-250'>
      <ModalHeader contentClassName='rebrand:text-text-inverse'>Need help?</ModalHeader>
      {/*
       * `color-scheme: dark` hands the native scroll UI over to the browser's dark
       * rendering so its track stops sitting on a light background inside the dark modal.
       */}
      <ModalBody className='sm:py-8 flex flex-col gap-6 rebrand:[color-scheme:dark]'>
        <Body.Default className='text-medium font-normal text-neutral-800 rebrand:text-text-inverse' as='h2'>
          Common issues
        </Body.Default>
        <ol className='list-style-none flex flex-col gap-6'>
          {AudioVideoTroubleshootActionItem}
          <HelpModalItem
            heading='My client is trying to get into the session'
            description={
              <>
                When a client is in the waiting room, you’ll be notified via a message in the bottom left of your
                screen. Select <span className='font-medium'>“View”</span> to open the client queue, and then select
                client(s) to <span className='font-medium'>“Admit.”</span> If the message disappears, select the{' '}
                <span className='font-medium'>“Clients”</span> button to open the client queue.
              </>
            }
          />
          <HelpModalItem
            heading="My client isn't here"
            description={
              <>
                Your client has a 15-minute grace period. When 15 minutes have passed, see our{' '}
                <Link
                  external
                  className='text-neutral-800 hover:text-neutral-900 rebrand:text-action-primary-textLinkInverse rebrand:hover:text-action-primary-textLinkInverseHover'
                  href={PROVIDER_CLIENT_NO_SHOW_ARTICLE}
                >
                  no-show guidelines
                </Link>{' '}
                for next steps.
              </>
            }
          />
          <HelpModalItem
            heading='I need help with a different issue'
            description={
              <>
                See our{' '}
                <Link
                  external
                  className='text-neutral-800 hover:text-neutral-900 rebrand:text-action-primary-textLinkInverse rebrand:hover:text-action-primary-textLinkInverseHover'
                  href={PROVIDER_TELEHEALTH_HELP_ARTICLE}
                >
                  Telehealth guide
                </Link>{' '}
                for additional help. If your client is experiencing issues, they may use the{' '}
                <Link
                  external
                  externalIcon={null}
                  className='text-neutral-800 hover:text-neutral-900 rebrand:text-action-primary-textLinkInverse rebrand:hover:text-action-primary-textLinkInverseHover'
                  href={CLIENT_TELEHEALTH_HELP_ARTICLE}
                >
                  Client Telehealth guide
                </Link>{' '}
                <CopyButton copyText={CLIENT_TELEHEALTH_HELP_ARTICLE} label='Copy Client Telehealth Guide' />
              </>
            }
          />
        </ol>
      </ModalBody>

      <ModalFooter
        actions={[
          {
            key: 'close-modal',
            use: 'secondary',
            onClick: onClose,
            actionContent: 'Done',
          },
        ]}
      >
        <Body.Small className='text-small rebrand:text-text-inverse'>
          If you need additional support, reach out to support in your provider portal.
        </Body.Small>
      </ModalFooter>
    </Modal>
  );
}

export function HelpModalWrapper() {
  useInitAvailableDevices();
  const [isHelpModalOpen, setIsHelpModalOpen] = useAtom(providerIsHelpModalOpenAtom);
  const setDrawerState = useSetAtom(drawerStateAtom);
  const openSettings = () => {
    setIsHelpModalOpen(false);
    setDrawerState(ProviderDrawerState.SETTINGS);
  };

  return (
    <HelpModal
      isOpen={isHelpModalOpen}
      onClose={() => setIsHelpModalOpen(false)}
      audioVideoTroubleshootActionItem={<BaseAudioVideoTroubleshootActionItem onOpenSettings={openSettings} />}
    />
  );
}
